/**
 * Mobile SPA compatibility shim for TanStack Start server functions.
 *
 * In the SSR web build `createServerFn` is imported from `@tanstack/react-start`
 * and executed server-side via Nitro. In the mobile SPA build we alias that
 * import to this module so the same wallet/referral code keeps working — the
 * shim proxies all calls to the deployed Vercel backend's /_server/* RPC
 * endpoints, which is where the real server functions live.
 *
 * The Vite mobile config (`vite.config.mobile.ts`) sets up the alias:
 *   `@tanstack/react-start` → `src/lib/server-fn-shim.ts`
 */

// No static imports of supabase or capacitor-platform to avoid build-time circular dependencies

// ---------------------------------------------------------------------------
// Types matching TanStack Start's createServerFn builder surface
// ---------------------------------------------------------------------------

interface ServerFnBuilder {
  middleware: (mws: any[]) => ServerFnBuilder;
  validator: (fn: (input: any) => any) => ServerFnBuilder;
  inputValidator: (fn: (input: any) => any) => ServerFnBuilder;
  handler: (fn: (...args: any[]) => any) => (...args: any[]) => Promise<any>;
}

// ---------------------------------------------------------------------------
// Registry: maps handler IDs to their server endpoint paths
// ---------------------------------------------------------------------------

// We track a counter so each createServerFn gets a unique ID.
// TanStack Start uses file paths + export names, but for the mobile shim
// we assign monotonic IDs and call the production backend generically.
let _fnCounter = 0;

/**
 * Drop-in replacement for `createServerFn`.
 *
 * Since the handlers use `supabaseAdmin` (service role key) and server-only
 * middleware, they CANNOT run in the client. Instead, we proxy every call
 * to the deployed Vercel backend which has the real server functions.
 *
 * TanStack Start server functions are exposed as HTTP POST endpoints.
 * The client SDK calls `POST /_server/?_serverFnId=<id>&_serverFnName=<name>`
 * with JSON body `{ data: <input> }`.
 *
 * Since we don't know the exact server function IDs at build time (they're
 * generated by TanStack Start's compiler), we instead call the production
 * backend through a thin API proxy. Each function is called using fetch
 * to the production URL, which handles SSR routing.
 */
export function createServerFn(opts?: { method?: string }): ServerFnBuilder {
  const fnId = _fnCounter++;
  let _validator: ((input: any) => any) | null = null;
  let _method = opts?.method ?? "POST";

  const builder: ServerFnBuilder = {
    middleware(_mws: any[]) {
      // Middleware runs server-side only; skip on mobile
      return builder;
    },
    validator(fn: (input: any) => any) {
      _validator = fn;
      return builder;
    },
    inputValidator(fn: (input: any) => any) {
      _validator = fn;
      return builder;
    },
    handler(_handlerFn: (...args: any[]) => any) {
      // Return a callable that proxies to the production server or runs locally if possible
      return async (input?: any) => {
        const validated = _validator ? _validator(input) : input;

        // Dynamically import dependencies to break static circular references
        const { supabase } = await import("@/integrations/supabase/client");
        const { getApiBaseUrl } = await import("./capacitor-platform");

        // Get auth token for the request
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        if (!token) {
          throw new Error("Authentication required. Please sign in.");
        }

        // Attempt to execute the handler function directly on the client first.
        // In the SPA/mobile build, many server functions are designed to do Supabase operations
        // that can run client-side. We pass the client-side supabase client and userId in context.
        try {
          const context = {
            supabase,
            userId: session.user.id,
            claims: session.user.app_metadata || {},
          };
          return await _handlerFn({ context, data: validated });
        } catch (localErr: any) {
          console.warn("Local execution of server function failed, falling back to server fetch:", localErr);

          const baseUrl = getApiBaseUrl();
          if (!baseUrl) {
            // If no server base URL is configured (e.g., local web development SPA),
            // we cannot fall back to a remote server, so we propagate the local error.
            throw localErr;
          }

          // TanStack Start server functions are POST endpoints at /_server
          // The request body format is the function input data.
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          };

          const url = `${baseUrl}/_server`;

          try {
            const response = await fetch(url, {
              method: _method === "GET" ? "POST" : "POST", // TanStack Start always uses POST for RPC
              headers,
              body: JSON.stringify({ data: validated }),
            });

            if (!response.ok) {
              const text = await response.text();
              throw new Error(text || `Server error: ${response.status}`);
            }

            return await response.json();
          } catch (err: any) {
            // If the production server isn't reachable, provide a helpful error
            if (err.name === "TypeError" && err.message.includes("fetch")) {
              throw new Error(
                "Cannot reach WeFest servers. Please check your internet connection."
              );
            }
            throw err;
          }
        }
      };
    },
  };

  return builder;
}

// ---------------------------------------------------------------------------
// Re-exports for other modules that import from @tanstack/react-start
// ---------------------------------------------------------------------------

/**
 * Mobile shim for createMiddleware.
 * Used by auth-attacher.ts and auth-middleware.ts.
 * On mobile these are no-ops since auth is handled differently.
 */
export function createMiddleware(_opts?: any) {
  return {
    client(fn: any) {
      // Return the middleware function itself as a passthrough
      return fn;
    },
    server(fn: any) {
      // Server middleware is a no-op on mobile
      return fn;
    },
  };
}

/**
 * Mobile shim for createStart.
 * Used by start.ts — on mobile this is a no-op.
 */
export function createStart(fn: any) {
  return fn?.() ?? {};
}

/**
 * Mobile shim for useServerFn.
 * In TanStack Start this wraps a server function for client-side use.
 * Our shim's createServerFn already returns a callable async function,
 * so useServerFn is just a passthrough.
 */
export function useServerFn<T extends (...args: any[]) => any>(fn: T): T {
  return fn;
}
